import { Box, CircularProgress, Typography } from '@mui/material';
import { type ReactNode, useCallback } from 'react';

import type { DomainFilter, NumericRange } from '@verantos-dev/types-cohorts';

import { ConceptRefRowRenderer } from './ConceptRefRowRenderer';
import { EditableNumericFilter } from './EditableNumericFilter';
import { LogicConnector } from './LogicConnector';
import { LogicNodeRenderer } from './LogicNodeRenderer';
import { useDomainFilterCard } from './useDomainFilterCard';
import { extractConceptNames, getTableLabel } from '../../utils/analyticalVariable.utils';

interface DomainFilterCardProps {
  filter: DomainFilter;
  onChipClick: (name: string) => void;
  isCorrelatedFilter?: boolean;
}

export const DomainFilterCard = ({
  filter,
  onChipClick,
  isCorrelatedFilter = false,
}: DomainFilterCardProps) => {
  const {
    intl,
    conceptGroups,
    conceptGroupsByDomain,
    isEditable,
    unavailableTooltip,
    conceptGroupIdToNameMap,
    conceptGroupNameToOwnerMap,
    conceptRefRows,
    numericFields,
    correlatedFilters,
    getEditedNames,
    addName,
    removeName,
    flushPendingChanges,
    updateNumericField,
    autoOpenRowIdx,
    isConceptGroupsLoading,
    isSaving,
    savingTooltip,
  } = useDomainFilterCard(filter);

  // TODO: re-enable after release — numeric filter editing temporarily disabled
  const _handleNumericChange = useCallback(
    (fieldName: string, updated: NumericRange) => {
      updateNumericField(fieldName, updated);
    },
    [updateNumericField]
  );

  const items: ReactNode[] = [];

  conceptRefRows.forEach((row, idx) => {
    const names =
      getEditedNames(idx) ?? extractConceptNames(row.conceptRef, conceptGroupIdToNameMap);
    if (names.length === 0 && !isEditable) return;

    items.push(
      <ConceptRefRowRenderer
        key={`ref-${idx}`}
        row={row}
        onChipClick={onChipClick}
        conceptGroupIdToNameMap={conceptGroupIdToNameMap}
        conceptGroupNameToOwnerMap={conceptGroupNameToOwnerMap}
        unavailableTooltip={unavailableTooltip}
        isEditable={isEditable}
        isSaving={isSaving}
        savingTooltip={savingTooltip}
        availableConceptGroups={conceptGroups}
        conceptGroupsByDomain={conceptGroupsByDomain}
        onAdd={(name) => addName(idx, row.conceptRef, name)}
        onRemove={(name) => removeName(idx, row.conceptRef, name)}
        onDropdownClose={() => flushPendingChanges(idx, row.conceptRef)}
        editedNames={getEditedNames(idx)}
        isConceptGroupsLoading={isConceptGroupsLoading}
        forceOpenDropdown={autoOpenRowIdx === idx}
      />
    );
  });

  numericFields.forEach(({ field: fieldName, labelId, filter: numFilter }) => {
    items.push(
      <Box
        key={labelId}
        data-testid="av-logic-numeric-filter"
        sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
      >
        <Typography
          data-testid="av-logic-numeric-filter-label"
          variant="body2"
          color="text.primary"
        >
          {intl.formatMessage({ id: labelId, defaultMessage: labelId }, { filter: '' }).trim()}
        </Typography>
        <EditableNumericFilter
          filter={numFilter}
          // TODO: re-enable after release — numeric filter editing temporarily disabled
          readOnly
        />
      </Box>
    );
  });

  const hasFilters = items.length > 0 || correlatedFilters.length > 0;

  return (
    <Box
      data-testid="av-logic-domain-filter"
      sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      {!isCorrelatedFilter ? (
        <Typography variant="body2" color="text.primary" fontWeight={400}>
          {intl.formatMessage(
            {
              id: hasFilters
                ? 'projects.analyticalVariables.logic.tableRecord'
                : 'projects.analyticalVariables.logic.allTableRecords',
            },
            { table: getTableLabel(intl, filter.table) }
          )}
        </Typography>
      ) : null}
      {hasFilters ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map((item, idx) => (
            <Box key={idx} data-testid={`av-logic-domain-filter-item-${idx}`}>
              {idx > 0 && (
                <LogicConnector
                  label={intl
                    .formatMessage({ id: 'projects.analyticalVariables.logic.andOperator' })
                    .toUpperCase()}
                  emphasis="secondary"
                />
              )}
              {item}
            </Box>
          ))}

          {isConceptGroupsLoading && conceptRefRows.length > 0 && (
            <Box
              data-testid="av-logic-resolving-concept-groups"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}
            >
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                {intl.formatMessage({
                  id: 'projects.analyticalVariables.logic.resolvingConceptGroups',
                })}
              </Typography>
            </Box>
          )}

          {correlatedFilters.map((corr, idx) => (
            <Box key={`correlated-${idx}`} data-testid={`av-logic-correlated-filter-${idx}`}>
              {(items.length > 0 || idx > 0) && (
                <LogicConnector
                  label={intl
                    .formatMessage({ id: 'projects.analyticalVariables.logic.andOperator' })
                    .toUpperCase()}
                  emphasis="secondary"
                />
              )}
              <LogicNodeRenderer node={corr.filter} onChipClick={onChipClick} isCorrelatedFilter />
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};
